/**
 * BlockShipmentModal - Modal para bloquear un shipment
 *
 * Solicita motivo de bloqueo (desde /invoices/block-reasons) y comentario
 * antes de enviar a POST /invoices/shipments/{id}/block
 */
import { Button } from '@shared/components/ui/button';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter/types';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockReasons } from '../../hooks/useBlockReasons';

export interface BlockShipmentModalRef {
  open: (shipmentId: number, onConfirm: (data: { shipment_block_reason_id: string; comment: string }) => void) => void;
  close: () => void;
}

export const BlockShipmentModal = forwardRef<BlockShipmentModalRef>((_, ref) => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { data: reasons = [], isLoading } = useBlockReasons();

  const [confirmCb, setConfirmCb] = useState<
    ((data: { shipment_block_reason_id: string; comment: string }) => void) | null
  >(null);
  const [reasonId, setReasonId] = useState('');
  const [comment, setComment] = useState('');

  useImperativeHandle(
    ref,
    () => ({
      open: (_id, onConfirm) => {
        setConfirmCb(() => onConfirm);
        setReasonId('');
        setComment('');
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }),
    [],
  );

  const handleConfirm = () => {
    if (!reasonId || !comment || !confirmCb) return;
    confirmCb({ shipment_block_reason_id: reasonId, comment });
    modalRef.current?.closeModal();
  };

  const options = reasons.map((r) => ({ label: r.name, value: r.id }));

  return (
    <ModalCenter
      ref={modalRef}
      title={t('confirmModals.blockShipment.title', 'Bloquear shipment')}
      width="lg"
      type="warning"
      data-testid="block-shipment-modal"
    >
      <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
        {/* Formulario - Motivo y Comentario */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Motivo */}
          <div className="space-y-1">
            <span className="text-sm">{t('confirmModals.blockShipment.reason', 'Motivo de bloqueo')}</span>
            <Combobox
              options={options}
              value={reasonId}
              setValue={setReasonId}
              placeholder={t('confirmModals.blockShipment.reasonPlaceholder', 'Seleccionar motivo...')}
              className="w-full"
              disabled={isLoading}
              dataTestId="block-reason-combobox"
            />
          </div>

          {/* Comentario */}
          <div className="space-y-1">
            <span className="text-sm">{t('confirmModals.blockShipment.comment', 'Comentario')}</span>
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('confirmModals.blockShipment.commentPlaceholder', 'Describe el motivo...')}
              data-testid="block-comment-input"
            />
          </div>
        </div>

        {/* Acciones */}
        <div className="flex justify-end mt-4 gap-2">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()} data-testid="block-cancel">
            {t('confirmModals.blockShipment.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!reasonId || !comment}
            data-testid="block-confirm"
          >
            {t('confirmModals.blockShipment.confirm', 'Bloquear')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

BlockShipmentModal.displayName = 'BlockShipmentModal';
